import { collection, query, where, getDocs, addDoc, deleteDoc, serverTimestamp, Timestamp } from 'firebase/firestore'
import { db } from '@/lib/firebase'

export type FavoriteType = 'news' | 'listing' | 'group'

export type FavoriteItem = {
  id: string
  itemId: string
  type: FavoriteType
  title?: string
  url?: string
  savedAt?: Timestamp
}

export async function addFavorite(userId: string, type: FavoriteType, itemId: string, payload?: { title?: string; url?: string }): Promise<void> {
  if (!userId) return
  const col = collection(db, 'users', userId, 'favorites')
  // Avoid duplicates by checking existing
  const q = query(col, where('itemId', '==', itemId), where('type', '==', type))
  const snap = await getDocs(q)
  if (!snap.empty) {
    return
  }
  await addDoc(col, {
    itemId,
    type,
    title: payload?.title ?? '',
    url: payload?.url ?? '',
    savedAt: serverTimestamp()
  })
}

export async function removeFavorite(userId: string, type: FavoriteType, itemId: string): Promise<void> {
  if (!userId) return
  const col = collection(db, 'users', userId, 'favorites')
  const q = query(col, where('itemId', '==', itemId), where('type', '==', type))
  const snap = await getDocs(q)
  for (const d of snap.docs) {
    await deleteDoc(d.ref)
  }
}

export async function getFavorites(userId: string): Promise<{ news: FavoriteItem[]; listing: FavoriteItem[]; group: FavoriteItem[] }> {
  const result = { news: [] as FavoriteItem[], listing: [] as FavoriteItem[], group: [] as FavoriteItem[] }
  if (!userId) return result
  const col = collection(db, 'users', userId, 'favorites')
  const snap = await getDocs(col)
  snap.docs.forEach((doc) => {
    const data = doc.data()
    const type = data.type as FavoriteType
    const favorite: FavoriteItem = {
      id: doc.id,
      itemId: data.itemId as string,
      type,
      title: data.title as string | undefined,
      url: data.url as string | undefined,
      savedAt: data.savedAt as Timestamp | undefined,
    }
    if (type === 'news') {
      result.news.push(favorite)
    } else if (type === 'listing') {
      result.listing.push(favorite)
    } else if (type === 'group') {
      result.group.push(favorite)
    }
  })
  return result
}

export async function hasFavorites(userId: string): Promise<boolean> {
  if (!userId) return false
  const fav = await getFavorites(userId)
  const total = (fav.news?.length || 0) + (fav.listing?.length || 0) + (fav.group?.length || 0)
  return total > 0
}
